import type { ErrorLike } from '@apollo/client'
import { useContext } from 'react'
import { UserContext } from '../../../../../common/context/User'
import useStartTrial from '../../../../../common/hooks/useStartTrial'
import useUpdateSubscriptionQuantityByChannelIds, {
  UpdatePhase,
} from '../../../../../common/hooks/useUpdateSubscriptionQuantityByChannelIds'
import { MODALS } from '../../../../../common/types'
import {
  isFreeUser,
  isOnActiveTrial,
  userCanStartFreeTrial,
} from '../../../../../common/utils/user'
import trackCTAClicked from '../../../../../tracking/UpgradePaths/trackCTAClicked'
import useChannels from '../../../hooks/useChannels'
import { useModalManager } from '../../../../ModalManager/hooks/useModalManager'

type UseButtonReturn = {
  handleSkipUpgrading: () => void
  handleClick: () => void
  actionError: ErrorLike | Error | { message: string } | null
  isProcessing: boolean
  actionLabel: string
}

type UseButtonsProps = {
  cta: string
  upgradePathName: string
}

// Click Handlers
const useActions = ({
  cta,
  upgradePathName,
}: UseButtonsProps): UseButtonReturn => {
  const user = useContext(UserContext)
  const canStartTrial = userCanStartFreeTrial(user)
  const isFree = isFreeUser(user)
  const { openModal } = useModalManager()
  const { connectedChannelIds, lockedChannelIds } = useChannels()
  const isOnTrial = isOnActiveTrial(user)

  // Handler when skipping upgrading or starting trial
  const handleSkipUpgrading = (): void => {
    openModal({
      key: MODALS.channelConnectionSuccessModal,
      data: {
        ctaView: 'connectChannelUpgradeModal',
        ctaLocation: 'channelsPage',
        cta,
        ctaVersion: '1',
        channelIds: connectedChannelIds,
        upgradePathName,
      },
    })
  }

  // Handler when starting trial
  const {
    startTrial: handleStartTrial,
    error: startTrialError,
    processing: startTrialProcessing,
  } = useStartTrial({
    user,
    attribution: { cta, upgradePathName },
  })

  // Handler when upgrading
  const {
    updateSubscriptionQuantityByChannelIds,
    isProcessing: upgradeProcessing,
    error: upgradeError,
    updatePhase,
  } = useUpdateSubscriptionQuantityByChannelIds({
    user,
    channelIds: lockedChannelIds,
    cta: `${cta}-connectChannelUpgradeModal-upgrade-1`,
    upgradePathName,
  })

  function handleUpgrade(): void {
    trackCTAClicked({
      payload: {
        cta,
        upgradePathName,
      },
      user,
    })
    if (isOnTrial || isFree) {
      openModal({
        key: MODALS.planSelector,
        data: {
          ctaView: 'connectChannelUpgradeModal',
          ctaLocation: 'channelsPage',
          cta,
          ctaVersion: '1',
          upgradePathName,
        },
      })
    } else {
      updateSubscriptionQuantityByChannelIds()
    }
  }

  // Compute dynamic action label based on phase (for upgrade flow)
  const getUpgradeActionLabel = (): string => {
    if (upgradeProcessing) {
      if (updatePhase === UpdatePhase.Updating) {
        return 'Updating subscription'
      }
      if (updatePhase === UpdatePhase.Authorizing) {
        return 'Authorizing payment'
      }
      if (updatePhase === UpdatePhase.Confirming) {
        return 'Confirming payment'
      }
    }
    // Default labels when not processing
    return isFree ? 'Upgrade Plan' : 'Confirm and Pay'
  }

  return {
    handleSkipUpgrading,
    handleClick: canStartTrial ? handleStartTrial : handleUpgrade,
    actionError: canStartTrial ? startTrialError : upgradeError,
    isProcessing: canStartTrial ? startTrialProcessing : upgradeProcessing,
    actionLabel: canStartTrial ? 'Start Trial' : getUpgradeActionLabel(),
  }
}

export default useActions
